import { z } from "zod";

/**
 * URL scheme safety.
 *
 * `zod`'s `.url()` (and a bare `new URL()`) happily accept `javascript:` and
 * `data:` URLs. Any such value that later lands in an `href`/`src` is a stored
 * XSS vector, and any value fed to `window.location`/server-side `fetch` is an
 * open-redirect / SSRF vector. Every attacker-supplied URL that we render or
 * navigate to must be constrained to http(s).
 *
 * Two layers use this:
 *   • Input validation — swap `z.string().url()` for {@link httpUrl} in the
 *     server-action schemas so bad values never persist.
 *   • Output defence — wrap render sites in {@link safeHttpUrl} so legacy rows
 *     written before this validation existed are defanged too.
 */

const SAFE_PROTOCOLS = new Set(["http:", "https:"]);

/** True only for an absolute http(s) URL string. */
export function isSafeHttpUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0) return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return SAFE_PROTOCOLS.has(parsed.protocol);
}

/**
 * Returns the value when it's a safe http(s) URL, otherwise null. Use at any
 * render site that puts an attacker-controlled value into an `href`/`src` or
 * hands it to `window.location`.
 */
export function safeHttpUrl(value: unknown): string | null {
  return isSafeHttpUrl(value) ? value : null;
}

/**
 * Zod schema for an http(s) URL string. Drop-in replacement for
 * `z.string().url()`; callers add `.nullable().optional()` as needed.
 */
export const httpUrl = z
  .string()
  .refine(isSafeHttpUrl, "Enter a valid http(s) URL");

/**
 * Constrain a post-auth `?next=` redirect target to a same-origin path.
 * Rejects absolute URLs (`https://evil.com`), scheme-relative (`//evil.com`),
 * and backslash tricks (`/\evil.com`) — anything that isn't a plain path
 * beginning with a single `/` falls back to the default.
 */
export function safeNextPath(
  value: string | null | undefined,
  fallback = "/dashboard"
): string {
  if (!value || !value.startsWith("/")) return fallback;
  if (value.startsWith("//") || value.startsWith("/\\")) return fallback;
  return value;
}
